import {Employee, KeySnapshot, KeyTransaction, KeyVault, User} from "../../api/Model";
import {locationTypeOptions} from "../../components/location/KeyLocationShield";
import {today} from "../../util/DateUtil";
import {OverridableComponent} from "@mui/material/OverridableComponent";
import {Stack, SvgIconTypeMap} from "@mui/material";
import {ReactNode} from "react";
import {KeyVaultChip} from "../../components/key-vault-chip/KeyVaultChip";
import {patchKey} from "../../api/KeyTransactionStore";

export type WizardKeyAction = {
    key: string,
    label: string,
    tooltip: string,
    submitLabel: string,
    icon: OverridableComponent<SvgIconTypeMap>,
    payload: WizardActionPayload,
}

export class ValidationError extends Error {
    constructor(public message: string, public field?: string) {
        super(message);
    }
}

async function submitGiveToPerson(
    user: User | null,
    keySnapshot: KeySnapshot,
    payload: SelectPersonActionPayload,
    date: string,
    notes: string,
) {
    if (payload.personType === 'employee' && !payload.employee) {
        throw new ValidationError('Please select an employee', 'employee');
    }
    if (payload.personType === 'guest' && !payload.guestFullName.trim()) {
        throw new ValidationError('Please enter contractor full name', 'guestFullName');
    }
    if (payload.issuanceType === 'temporary' && !payload.expToDate) {
        throw new ValidationError('Please select an expected return date', 'expToDate');
    }
    if (payload.keyReasons.some(reason => !reason.reason)) {
        throw new ValidationError('Please select a reason for issuing another key', 'keyReasons');
    }

    const expTo = payload.issuanceType === 'temporary' ? payload.expToDate : undefined;

    const issueKeyTransaction = patchKey(keySnapshot, date, {
        location: payload.personType === 'employee' && payload.employee ? {
            type: 'with-employee',
            employee: payload.employee,
            expTo,
        } : {
            type: 'with-guest',
            fullName: payload.guestFullName,
            company: payload.guestCompany,
            expTo,
        },
        notes,
    });

    const lostKeyTransactions = payload.keyReasons
        .filter(keyReason => keyReason.reason?.key === 'lost')
        .map(keyReason => patchKey(keyReason.employeeKey, today(), {
            location: {type: "lost"},
            notes: `Replaced by key ${keySnapshot.key.keyNumber}`,
        }));

    const damagedKeyTransactions = payload.keyReasons
        .filter(keyReason => keyReason.reason?.key === 'damaged')
        .map(keyReason => patchKey(keyReason.employeeKey, today(), {
            location: {type: "damaged"},
            notes: `Replaced by key ${keySnapshot.key.keyNumber}`,
        }));

    return {
        transactions: [
            issueKeyTransaction,
            ...lostKeyTransactions,
            ...damagedKeyTransactions
        ],
        successMessage: 'Key issued successfully!',
        failureMessage: 'Failed to issue a new key. Please try again.',
    }
}

async function moveToVault(
    user: User | null,
    keySnapshot: KeySnapshot,
    payload: SelectVaultActionPayload,
    date: string,
    notes: string,
) {
    return {
        transactions: [patchKey(keySnapshot, date, {
            vault: payload.targetVault,
            notes,
        })],
        successMessage: 'Key moved successfully!',
        failureMessage: 'Failed to move key. Try again later.',
    }
}

export const noopAction: WizardKeyAction = {
    key: 'noop',
    label: 'No action',
    tooltip: 'No key action is available',
    submitLabel: 'Noop',
    icon: locationTypeOptions['deactivated'].icon,
    payload: {
        type: 'noop',
        submit: () => Promise.resolve({
            transactions: [],
            successMessage: '',
            failureMessage: '',
        }),
    },
}

function createGiveToPerson(): WizardKeyAction {
    return {
        key: 'give-to-person',
        label: 'Give to',
        tooltip: 'Issue the key to an employee or contractor',
        submitLabel: 'Give',
        icon: locationTypeOptions['with-employee'].icon,
        payload: {
            type: 'select-person',
            personType: 'employee',
            employee: undefined,
            guestFullName: '',
            guestCompany: '',
            keyReasons: [],
            issuanceType: 'temporary',
            expToDate: undefined,
            submit: submitGiveToPerson
        },
    }
}

function createReturnToVault(keySnapshot: KeySnapshot): WizardKeyAction {
    return {
        key: 'return-to-vault',
        label: 'Collect Key',
        tooltip: 'Collect the key from its current holder and return it to its vault',
        submitLabel: 'Collect',
        icon: locationTypeOptions['with-vault'].icon,
        payload: {
            type: 'apply-directly',
            description: (
                <Stack alignItems="center" gap={1} direction="row">
                    Return key to vault <KeyVaultChip keyVault={keySnapshot.historyRecord.vault} size="small"/>
                </Stack>
            ),
            async submit(_user, keySnapshot, _payload, date, notes) {
                return {
                    transactions: [patchKey(keySnapshot, date, {
                        location: {type: 'with-vault'},
                        notes: notes,
                    })],
                    successMessage: 'Key returned to vault successfully!',
                    failureMessage: 'Failed to return key to vault',
                };
            }
        },
    }
}

function createMoveToVault(keySnapshot: KeySnapshot): WizardKeyAction {
    return {
        key: 'move-to-vault',
        label: 'Move to',
        tooltip: 'Assign the key to another vault',
        submitLabel: 'Move',
        icon: locationTypeOptions['with-vault'].icon,
        payload: {
            type: 'select-vault',
            targetVault: keySnapshot.historyRecord.vault,
            submit: moveToVault,
        },
    }
}

function createMarkLost(): WizardKeyAction {
    return {
        key: 'mark-lost',
        label: 'Lost',
        tooltip: 'Mark the key as lost',
        submitLabel: 'Mark as Lost',
        icon: locationTypeOptions['lost'].icon,
        payload: {
            type: 'apply-directly',
            description: 'Mark Key as Lost',
            async submit(_user, keySnapshot, _payload, date, notes) {
                return {
                    transactions: [patchKey(keySnapshot, date, {
                        location: {type: 'lost'},
                        notes: notes,
                    })],
                    successMessage: 'Key marked as lost successfully',
                    failureMessage: 'Failed to mark key as lost',
                };
            }
        },
    };
}

function createMarkDamaged(): WizardKeyAction {
    return {
        key: 'mark-damaged',
        label: 'Damaged',
        tooltip: 'Mark the key as damaged or not working',
        submitLabel: 'Mark as Damaged',
        icon: locationTypeOptions['damaged'].icon,
        payload: {
            type: 'apply-directly',
            description: 'Mark Key as Damaged',
            async submit(_user, keySnapshot, _payload, date, notes) {
                return {
                    transactions: [patchKey(keySnapshot, date, {
                        location: {type: 'damaged'},
                        notes: notes,
                    })],
                    successMessage: 'Key marked as damaged successfully',
                    failureMessage: 'Failed to mark key as damaged',
                };
            },
        },
    };
}

function createMarkDeactivated(): WizardKeyAction {
    return {
        key: 'mark-deactivated',
        label: 'Deactivate',
        tooltip: 'Deactivate the key so it is no longer in use',
        submitLabel: 'Mark as Deactivated',
        icon: locationTypeOptions['deactivated'].icon,
        payload: {
            type: 'apply-directly',
            description: 'Mark Key as Deactivated',
            async submit(_user, keySnapshot, _payload, date, notes) {
                return {
                    transactions: [patchKey(keySnapshot, date, {
                        location: {type: 'deactivated'},
                        notes: notes,
                    })],
                    successMessage: 'Key marked as deactivated successfully',
                    failureMessage: 'Failed to mark key as deactivated',
                };
            }

        },
    };
}

export function getAvailableActions(keySnapshot: KeySnapshot): WizardKeyAction[] {
    switch (keySnapshot.historyRecord.location.type) {
        case 'with-vault':
            return [
                createGiveToPerson(),
                createMoveToVault(keySnapshot),
                createMarkLost(),
                createMarkDamaged(),
                createMarkDeactivated(),
            ];
        case 'with-employee':
        case 'with-guest':
            return [
                createReturnToVault(keySnapshot),
                createGiveToPerson(),
                createMoveToVault(keySnapshot),
                createMarkLost(),
                createMarkDamaged(),
                createMarkDeactivated(),
            ];
        case 'lost':
        case 'damaged':
            return [
                createMarkDeactivated(),
                createReturnToVault(keySnapshot),
                createGiveToPerson(),
            ];
        case 'deactivated':
            return [
                createReturnToVault(keySnapshot),
                createGiveToPerson(),
            ];
    }
}

export type MissingKeyReason = {
    key: string,
    text: string,
};
export const missingKeyReasons: MissingKeyReason[] = [
    {key: 'forgot', text: 'Forgot the key at home'},
    {key: 'lost', text: 'Lost the key'},
    {key: 'damaged', text: 'The existing key is not working'},
];

export type SelectPersonActionPayload = {
    type: 'select-person',
    personType: 'employee' | 'guest',
    employee: Employee | undefined,
    guestFullName: string,
    guestCompany?: string,
    keyReasons: { employeeKey: KeySnapshot, reason: MissingKeyReason | undefined }[],
    issuanceType: 'permanent' | 'temporary',
    expToDate: string | undefined,
    submit: (
        user: User | null,
        keySnapshot: KeySnapshot,
        payload: SelectPersonActionPayload,
        date: string,
        notes: string,
    ) => Promise<{
        transactions: { keySnapshot: KeySnapshot, tx: KeyTransaction }[],
        successMessage: string,
        failureMessage: string,
    }>,
};

export type SelectVaultActionPayload = {
    type: 'select-vault',
    targetVault: KeyVault,
    submit: (
        user: User | null,
        keySnapshot: KeySnapshot,
        payload: SelectVaultActionPayload,
        date: string,
        notes: string,
    ) => Promise<{
        transactions: { keySnapshot: KeySnapshot, tx: KeyTransaction }[],
        successMessage: string,
        failureMessage: string,
    }>,
};

export type ApplyDirectlyActionPayload = {
    type: 'apply-directly',
    description: ReactNode,
    submit: (
        user: User | null,
        keySnapshot: KeySnapshot,
        payload: ApplyDirectlyActionPayload,
        date: string,
        notes: string,
    ) => Promise<{
        transactions: { keySnapshot: KeySnapshot, tx: KeyTransaction }[],
        successMessage: string,
        failureMessage: string,
    }>,
};

export type NoopActionPayload = {
    type: 'noop',
    submit: (
        user: User | null,
        keySnapshot: KeySnapshot,
        payload: NoopActionPayload,
        date: string,
        notes: string,
    ) => Promise<{
        transactions: { keySnapshot: KeySnapshot, tx: KeyTransaction }[],
        successMessage: string,
        failureMessage: string,
    }>,
};

export type WizardActionPayload =
    | SelectPersonActionPayload
    | SelectVaultActionPayload
    | ApplyDirectlyActionPayload
    | NoopActionPayload
    ;
